// Login.tsx
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Fonts from "../../ui/global/Fonts";
import Colors from "../../ui/global/Colors";
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { SupabaseClient, Session } from '@supabase/supabase-js'
import { useLocation, useNavigate } from 'react-router-dom'
import LoadingSpinner from "../../ui/components/LoadingSpinner";
import LegalFooter from "../legal/LegalFooter";
import Logo from "../../ui/components/Logo";
import { useConfig } from "../../utils/config";
import { navigateToWebsite } from "../../utils/urls";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100svh;
  width: 100%;
  background-color: ${Colors.black};
  padding: 10px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  background-color: ${Colors.white};
  border-radius: 8px;
  border: 1px solid ${Colors.grey1};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 16px;
`;

const Header = styled.h4`
  color: ${Colors.black};
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 24px;
  text-align: center;
  margin: 10px 0 20px 0;
`;

const ErrorMessage = styled.p`
  color: ${Colors.red};
  margin-bottom: 20px;
  font-size: 18px;
`;

const Title = styled.h2`
  color: ${Colors.grey8};
  margin-bottom: 16px;
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 18px;
`;

const Text = styled.p`
  color: ${Colors.grey7};
  margin-bottom: 16px;
  font-size: 18px;
`;

const buttonStyles = {
  width: '100%',
  borderRadius: '4px',
  backgroundColor: Colors.grey9,
  color: Colors.white,
  fontFamily: Fonts.lexendMedium.fontFamily,
  fontWeight: Fonts.lexendMedium.fontWeight,
  fontSize: '18px',
  padding: '10px 20px',
  cursor: 'pointer',
  border: 'none',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: Colors.grey8,
  },
  '&:active': {
    backgroundColor: Colors.black,
  }
};

const SignOutButton = styled.button`
  ${buttonStyles}
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 0;
`;

const FooterText = styled.p`
  color: ${Colors.grey6};
  font-family: ui-sans-serif, sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 4px 0;
`;

const FooterLink = styled.a`
  color: ${Colors.grey6};
  font-family: ui-sans-serif, sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 4px 0;
  text-decoration: underline;
  cursor: pointer;

  &:hover, &:active {
    color: ${Colors.grey8};
  }
`;

interface LoginProps {
  supabase: SupabaseClient
}

const Login = ({ supabase }: LoginProps) => {
  const config = useConfig();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const redirectHandledRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  // useEffect(() => {
  //   console.log('LOGIN location.pathname:', location.pathname);
  // }, [location.pathname]);

  // useEffect(() => {
  //   console.log('LOGIN location.state:', location.state);
  // }, [location.state]);

  // Store the redirect path in localStorage if present
  useEffect(() => {
    const state = location.state as { from?: Location };
    const params = new URLSearchParams(location.search);
    const fromParam = params.get('from');

    // console.log('fromParam', fromParam);
    // console.log('state', state);

    if (fromParam) {
      // console.log('Storing redirect path in useEffect:', fromParam);
      localStorage.setItem('redirectPath', fromParam);
    } else if (state?.from && !localStorage.getItem('redirectPath')) { // Only set if not already set
      // console.log('Storing redirect path in useEffect:', state.from.pathname);
      localStorage.setItem('redirectPath', state.from.pathname);
    }
  }, [location.state, location.search]);

  // Handle email confirmation logic
  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const params = new URLSearchParams(location.search);
      const token_hash = params.get('token_hash');
      const type = params.get('type');
      
      if (token_hash && type) {
        try {
          setLoading(true);
          setError(null);
          const { error } = await supabase.auth.verifyOtp({
            token_hash,
            type: type as any
          });
          if (error) throw error;
      
          // Handle password recovery redirect
          if (type === 'recovery') {
            navigate('/reset-password', { 
              state: { token_hash },
              replace: true 
            });
            return;
          }

          // If it's not a recovery, clear search params and allow session effect to handle redirect
          navigate(location.pathname, { replace: true });

        } catch (err: any) {
          console.error('Error verifying email:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    handleEmailConfirmation();
  }, [supabase, location.search, location.pathname, navigate]);


  // Initial session load and auth state change listener
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        setSession(session);
        setLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setSession(session);
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      mounted = false;
    };
  }, [supabase]);

  // Effect to handle redirects based on session changes
  useEffect(() => {
    // Only attempt redirect if session is present and redirect not handled yet
    // console.log('Session present:', !!session);
    // console.log('redirectHandledRef.current:', redirectHandledRef.current);
    // console.log('localStorage.getItem("redirectPath"):', localStorage.getItem('redirectPath'));
    // console.log('location.state.from:', (location.state as any)?.from?.pathname);

    if (session && !redirectHandledRef.current) {
        const state = location.state as { from?: Location };
        const redirectPath = localStorage.getItem('redirectPath');
        
        if (redirectPath) {
            // console.log('Redirecting to stored path:', redirectPath);
            redirectHandledRef.current = true; // Mark redirect as handled
            // Stripe redirect logic based on config.ENABLE_STRIPE_REDIRECTS
            if (redirectPath.includes('stripe.com')) {
                if (config.ENABLE_STRIPE_REDIRECTS) {
                  const url = `${redirectPath}?prefilled_email=${session.user.email}&client_reference_id=${session.user.id}`;
                  window.location.href = url;
                } else {
                  navigate('/pricing', { replace: true });
                }
            } else if (redirectPath.startsWith('/')) {
                navigate(redirectPath, { replace: true });
            } else {
                window.location.href = redirectPath;
            }
            localStorage.removeItem('redirectPath'); // Clear after use
        } else if (state?.from) {
            // console.log('Redirecting to state.from path:', state.from.pathname);
            redirectHandledRef.current = true; // Mark redirect as handled
            navigate(state.from.pathname, { replace: true });
        } else {
            // console.log('Redirecting to default root: /');
            redirectHandledRef.current = true; // Mark redirect as handled
            if (config.REDIRECT_TO_WEBSITE) {
                navigateToWebsite('/');
            } else {
                navigate('/');
            }
        }

        // Store user information in local storage upon successful login
        if (session.user?.email && session.user?.id) {
          localStorage.setItem('userEmail', session.user.email);
          localStorage.setItem('userId', session.user.id);
        }
    } else if (!session) {
        // Reset redirect handled ref if session is null (e.g., after logout)
        redirectHandledRef.current = false;
        // Also clear user-related localStorage items to ensure clean state on logout
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userId');
        localStorage.removeItem('subscription'); // Ensure subscription is also cleared
    }
  }, [session, navigate, location.state, config.ENABLE_STRIPE_REDIRECTS]);

  const renderAuth = () => (
    <PageWrapper>
        <Logo colorMode="dark" size={36}/>
        <Container>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Header>Log In</Header>
        <Auth
          supabaseClient={supabase}
          view="sign_in"
          providers={[ 'google' ]} /* 'google', 'discord', 'github', */
          theme="default"
          // Redirect back to the login page itself to correctly pick up redirectPath from localStorage
          redirectTo={window.location.origin + location.pathname + location.search + location.hash} 
          showLinks={false}
          socialLayout="vertical"
          appearance={{
            theme: ThemeSupa,
            variables: {
              default: {
                colors: {
                  brand: Colors.grey9,
                  brandAccent: Colors.grey8,
                  // Social button styling
                  defaultButtonBackground: Colors.background,
                  defaultButtonBorder: Colors.grey1,
                  defaultButtonText: Colors.black,
                },
              },
            },
            style: {
              divider: {
                // display: 'none',
              },
            },
          }}
          localization={{
            variables: {
              sign_in: {
                social_provider_text: "Continue with {{provider}}",
                email_label: 'Email address',
                password_label: 'Password',
                button_label: 'Log in',
                loading_button_label: 'Logging in...',
                email_input_placeholder: 'Your email address',
                password_input_placeholder: 'Your password',
                link_text: "Don't have an account? Sign up",
              },
            }
          }}
        />
        <Footer>
          <FooterText>Forgot your password? <FooterLink href="/forgot-password">Reset</FooterLink></FooterText>
          <FooterText>Don't have an account? <FooterLink href="/signup">Sign up</FooterLink></FooterText>
        </Footer>
      </Container>
      <LegalFooter />
    </PageWrapper>
  );

  if (loading) {
    return <LoadingSpinner />;
  } else if (!session || error) {
    return renderAuth();
  } else {
    // Show spinner if there's a session and no error, assuming a redirect will happen shortly.
    return <LoadingSpinner />;
  }
};

export default Login;